import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Phone,
  AlertTriangle,
  ShieldCheck,
  MapPin,
  Ambulance,
  Send,
  CheckCircle2,
  Clock,
  Activity,
} from "lucide-react";
import { toast } from "sonner";
import { SiteShell } from "@/components/site-shell";

export const Route = createFileRoute("/emergency")({
  head: () => ({
    meta: [
      { title: "24x7 Emergency SOS Animal Helpline — Manavta" },
      {
        name: "description",
        content:
          "Report injured cattle, stray animal accidents or bird ICU emergencies for immediate ambulance dispatch across Gujarat.",
      },
    ],
  }),
  component: EmergencyDesk,
});

const HELPLINES = [
  {
    city: "Ahmedabad Emergency Rescue",
    phone: "+91 99244 00000",
    partner: "Jivdaya Charitable Trust & Ahmedabad Panjrapole",
  },
  {
    city: "Gandhinagar Helpline",
    phone: "+91 98250 11111",
    partner: "People for Animals (PFA) Gandhinagar",
  },
  {
    city: "Surat Animal SOS Desk",
    phone: "+91 98980 22222",
    partner: "Surat Panjrapole & Rescue Cell",
  },
  {
    city: "Rajkot & Saurashtra Helpline",
    phone: "+91 94262 33333",
    partner: "Rajkot Mahajan Panjrapole Trust",
  },
];

function EmergencyDesk() {
  const [submitted, setSubmitted] = useState(false);
  const [reporterName, setReporterName] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [animalType, setAnimalType] = useState("Cow / Calf");
  const [condition, setCondition] = useState("Road Accident Injury");
  const [notes, setNotes] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reporterName.trim() || !phone.trim() || !location.trim()) {
      toast.error("Please enter reporter name, phone number, and incident location.");
      return;
    }
    setSubmitted(true);
    toast.success("Emergency alert dispatched! Nearby rescue team notified.");
  }

  return (
    <SiteShell>
      {/* Header Banner */}
      <section className="bg-gradient-to-r from-red-900 via-rose-900 to-[var(--maroon-deep)] text-white py-10 px-4">
        <div className="mx-auto max-w-5xl text-center space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold text-amber-300 backdrop-blur border border-white/10">
            <AlertTriangle className="h-4 w-4 text-amber-400" /> 24x7 Emergency SOS Animal Desk
          </div>
          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">
            Report Injured Animals & Dispatch Rescue
          </h1>
          <p className="text-sm text-white/80 max-w-xl mx-auto leading-relaxed">
            Report stray animal accidents, injured cattle, or downed birds. Our network dispatches
            verified ambulances & vet doctors in under 20 minutes.
          </p>
        </div>
      </section>

      {/* Main SOS Content */}
      <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <div className="grid gap-8 md:grid-cols-12">
          {/* Left: Instant SOS Form */}
          <div className="md:col-span-7">
            <div className="rounded-3xl border border-red-200 bg-card p-6 shadow-md space-y-5">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div className="flex items-center gap-2.5">
                  <div className="grid h-10 w-10 place-items-center rounded-2xl bg-red-100 text-red-700">
                    <Ambulance className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-display text-base font-bold text-red-950">
                      Dispatch SOS Rescue Unit
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Free emergency response funded by Manavta donors
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-extrabold text-red-700 border border-red-200 uppercase">
                  <Activity className="h-3 w-3 animate-pulse" /> Live SOS
                </span>
              </div>

              {submitted ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center space-y-3">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-600 text-white shadow-md">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-base font-bold text-emerald-900">
                    Rescue Unit Dispatched!
                  </h3>
                  <p className="text-xs text-emerald-800 leading-relaxed max-w-sm mx-auto">
                    Ambulance unit #GJ-01-AMB-481 is en route to <strong>{location}</strong>. ETA:{" "}
                    <strong>14 minutes</strong>. Our vet technician will contact you on{" "}
                    <strong>{phone}</strong>.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-2 text-xs font-bold text-emerald-700 hover:underline"
                  >
                    Report another emergency
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 text-xs">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="font-bold text-foreground mb-1 block">Your Name *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Amit Patel"
                        value={reporterName}
                        onChange={(e) => setReporterName(e.target.value)}
                        className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-foreground mb-1 block">Phone Number *</label>
                      <input
                        type="tel"
                        required
                        placeholder="+91 98765 43210"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 outline-none focus:ring-2 focus:ring-red-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-bold text-foreground mb-1 block">
                      Exact Incident Location / Landmark *
                    </label>
                    <div className="flex items-center rounded-xl border border-border bg-background px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-red-500">
                      <MapPin className="h-4 w-4 text-red-600 mr-2 shrink-0" />
                      <input
                        type="text"
                        required
                        placeholder="e.g. Near Iscon Cross Road, SG Highway, Ahmedabad"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="w-full bg-transparent outline-none font-medium"
                      />
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="font-bold text-foreground mb-1 block">Animal Species</label>
                      <select
                        value={animalType}
                        onChange={(e) => setAnimalType(e.target.value)}
                        className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <option value="Cow / Calf">Cow / Calf / Bull</option>
                        <option value="Injured Bird">Injured Bird (Pigeon / Kite)</option>
                        <option value="Stray Dog">Stray Dog / Puppy</option>
                        <option value="Donkey / Horse">Donkey / Horse</option>
                        <option value="Wildlife">Wildlife / Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="font-bold text-foreground mb-1 block">
                        Condition / Severity
                      </label>
                      <select
                        value={condition}
                        onChange={(e) => setCondition(e.target.value)}
                        className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 outline-none focus:ring-2 focus:ring-red-500 font-bold text-red-700"
                      >
                        <option value="Road Accident Injury">Road Accident Injury</option>
                        <option value="Severe Fracture">Severe Fracture / Bleeding</option>
                        <option value="Dehydration / Fainting">Dehydration / Fainting</option>
                        <option value="Entangled / Trapped">Entangled / Trapped</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="font-bold text-foreground mb-1 block">
                      Additional Details (Optional)
                    </label>
                    <textarea
                      rows={2}
                      placeholder="e.g. Animal is lying near highway divider, needs stretcher..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full rounded-xl border border-border bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-red-600 hover:bg-red-700 py-3.5 text-sm font-bold text-white shadow-lg active:scale-98 transition"
                  >
                    <Send className="h-4 w-4" /> DISPATCH EMERGENCY AMBULANCE NOW
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Right: Direct Phone Helplines */}
          <div className="md:col-span-5 space-y-4">
            <div className="rounded-3xl border border-border bg-card p-6 shadow-sm space-y-4">
              <h3 className="font-display text-base font-bold text-[var(--maroon-deep)] border-b border-border pb-3 flex items-center gap-2">
                <Phone className="h-4 w-4 text-[var(--maroon)]" /> Direct Toll-Free Helplines
              </h3>
              <div className="space-y-3">
                {HELPLINES.map((h, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-border/80 bg-muted/20 p-3.5 space-y-1"
                  >
                    <p className="text-xs font-bold text-foreground">{h.city}</p>
                    <a
                      href={`tel:${h.phone}`}
                      className="text-sm font-extrabold text-[var(--maroon)] hover:underline block"
                    >
                      {h.phone}
                    </a>
                    <p className="text-[10px] text-muted-foreground">{h.partner}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-2">
              <div className="flex items-center gap-2 text-amber-800 font-bold text-xs">
                <Clock className="h-4 w-4 text-amber-600 shrink-0" /> Average Response Time: 18 Mins
              </div>
              <p className="text-[11px] text-amber-900/80 leading-relaxed">
                Emergency ambulances carry mobile ICU kits, oxygen cylinders, bandaging materials,
                and trained vet technicians.
              </p>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
